import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Play, Square } from 'lucide-react';

const ProfessionalThinkingDemo = () => {
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const startThinking = () => {
    setIsLoading(true);
  };

  const stopThinking = () => {
    setIsLoading(false);
  };

  return (
    <div style={{
      padding: '40px',
      background: theme.primaryBg,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '32px'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #3b82f6, #10b981)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Simple AI Thinking Animation
        </h1>
        <p style={{
          fontSize: '1.1rem',
          color: theme.textSecondary,
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          Experience the new simple thinking animation for Oakland AI. 
          Clean, minimal, and professional.
        </p>
      </div>

      {/* Control Buttons */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <button
          onClick={startThinking}
          disabled={isLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)'
          }}
          onMouseEnter={e => {
            if (!isLoading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
            }
          }}
          onMouseLeave={e => {
            if (!isLoading) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
            }
          }}
        >
          <Play size={20} />
          Start AI Thinking
        </button>

        <button
          onClick={stopThinking}
          disabled={!isLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: !isLoading ? 'not-allowed' : 'pointer',
            opacity: !isLoading ? 0.6 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 16px rgba(239, 68, 68, 0.3)'
          }}
          onMouseEnter={e => {
            if (isLoading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
            }
          }}
          onMouseLeave={e => {
            if (isLoading) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.3)';
            }
          }}
        >
          <Square size={20} />
          Stop AI Thinking
        </button>
      </div>

      {/* Status Indicator */}
      <div style={{
        padding: '16px 24px',
        background: theme.modalBg,
        border: `1px solid ${theme.primaryBorder}`,
        borderRadius: '12px',
        fontSize: '14px',
        color: theme.textSecondary
      }}>
        Status: {isLoading ? 'AI is thinking...' : 'Ready to start'}
      </div>

      {/* Simple Thinking Animation */}
      {isLoading && (
        <div className="simple-thinking-container">
          {/* Simple AI Status */}
          <div className="simple-ai-status">
            <div className="simple-logo">Oakland AI</div>
            <div className="simple-status-text">Oakland AI is thinking...</div>
          </div>
          
          {/* Simple Progress Bar */}
          <div className="simple-progress">
            <div className="simple-progress-bar">
              <div className="simple-progress-fill"></div>
            </div>
          </div>
          
          {/* Simple Loading Dots */}
          <div className="simple-loading-dots">
            <div className="simple-dot"></div>
            <div className="simple-dot"></div>
            <div className="simple-dot"></div>
          </div>
        </div>
      )}

      {/* Features List */}
      <div style={{
        maxWidth: '800px',
        margin: '40px auto 0',
        padding: '32px',
        background: theme.modalBg,
        border: `1px solid ${theme.primaryBorder}`,
        borderRadius: '20px',
        boxShadow: `0 8px 32px ${theme.primaryShadow}`
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          marginBottom: '24px',
          color: theme.textPrimary,
          textAlign: 'center'
        }}>
          Simple & Clean Features
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          <div style={{
            padding: '20px',
            background: theme.cardBg,
            borderRadius: '12px',
            border: `1px solid ${theme.primaryBorder}`
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '12px',
              color: theme.textPrimary
            }}>🎯 Minimal Design</h3>
            <p style={{
              fontSize: '14px',
              color: theme.textSecondary,
              lineHeight: '1.5'
            }}>
              Clean, simple interface that focuses on essential elements
            </p>
          </div>
          
          <div style={{
            padding: '20px',
            background: theme.cardBg,
            borderRadius: '12px',
            border: `1px solid ${theme.primaryBorder}`
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '12px',
              color: theme.textPrimary
            }}>🚀 Oakland AI Logo</h3>
            <p style={{
              fontSize: '14px',
              color: theme.textSecondary,
              lineHeight: '1.5'
            }}>
              Beautiful gradient logo with subtle pulsing animation
            </p>
          </div>
          
          <div style={{
            padding: '20px',
            background: theme.cardBg,
            borderRadius: '12px',
            border: `1px solid ${theme.primaryBorder}`
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '12px',
              color: theme.textPrimary
            }}>📊 Progress Bar</h3>
            <p style={{
              fontSize: '14px',
              color: theme.textSecondary,
              lineHeight: '1.5'
            }}>
              Simple animated progress bar showing activity
            </p>
          </div>
          
          <div style={{
            padding: '20px',
            background: theme.cardBg,
            borderRadius: '12px',
            border: `1px solid ${theme.primaryBorder}`
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '12px',
              color: theme.textPrimary
            }}>✨ Smooth Animations</h3>
            <p style={{
              fontSize: '14px',
              color: theme.textSecondary,
              lineHeight: '1.5'
            }}>
              Subtle, smooth animations that don't distract
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalThinkingDemo;
